import React, {
  useContext, useEffect, useMemo, useRef, useState
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useRouter } from 'next/router';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import IVendorProductGroup, {
  IAttributeFilterInput,
  IAttributeOption,
} from '@models/VendorVariant/i-vendor-product-group';
import If from '@components/shared/conditional/If/If';
import ShopNavigationMenu, {
  IShopNavigationMenu,
} from '@components/shared/shop/ShopNavigationMenu';
import ShowAt from '@components/shared/conditional/ShowAt';
import AddToBoardModalContext from '@context/Modal/add-to-board-modal-context';
import boardItemTypes from '@constants/boardItemTypes';
import { useTheme } from '@components/theme';
import OurStories from '@components/shared/OurStories/OurStories';
import HideAt from '@components/shared/conditional/HideAt';
import {
  ICollectionWithProductCards,
} from '@components/pages/ShopLanding/model/collectionWithProductCards';
import useCartActions from '@hooks/User/useAddToCartActions';
import UserContext from '@context/User/user-context';
import getBoardIdFromRouter from '@components/pages/ProductV2/shared/utils/getBoardIdFromRouter';
import AddedToCartConfirmationModalContext from '@context/Modal/added-to-cart-confirmation-modal-context';
import { NoIndexVendorsList } from '@components/shared/shop/model/Vendors';
import Divider from '@components/shared/Divider/Divider';

import Error from '../../Error/Error';
import DefaultTemplate from '../../../shared/templates/DefaultTemplate/DefaultTemplate';
import VendorVariantContext from '../../../../contexts/VendorVariant/vendor-variant-context';
import getVendorVariantAnalyticsPayload from '../shared/utils/getVendorVariantAnalyticsPayload';
import buildJsonSchema from '../shared/utils/buildJsonSchema';
import useProductGroupState, { autoSelectVendorVariant } from './useProductGroupState';

import { createFacebookOpenGraph } from '../shared/utils/tracking';
import StickyHeader from '../shared/components/StickyHeader/StickyHeader';
import ProductBreadcrumbs from '../shared/components/ProductBreadcrumbs/ProductBreadcrumbs';
import ProductDetails from '../shared/components/ProductDetails/ProductDetails';
import FeaturedDesigns from '../shared/components/FeaturedDesigns/FeaturedDesigns';
import Reviews from '../shared/components/Reviews/Reviews';
import Footer from '../shared/components/Footer/Footer';
import styles from './ProductGroup.module.scss';
import CollectionWithProducts
  from '../shared/components/CollectionWithProducts/CollectionWithProducts';
import MobileImageZoom, { IZoomableImage } from '../shared/components/MobileImageZoom/MobileImageZoom';
import ProductSelectCards from '../shared/components/ProductSelectCards/ProductSelectCards';
import AttributesFilter from '../shared/components/AttributesFilter/AttributesFilter';

interface IPageProductProps {
  initialVendorVariantId?: string;
  initialVendorProductGroup: IVendorProductGroup;
  host: string;
  canonicalUrl: string;
  isIosNative: boolean;
  shopNavigationMenu: IShopNavigationMenu[];
  collectionWithProducts: Omit<ICollectionWithProductCards, 'vendorVariants'>;
}

export default function ProductGroup({
  initialVendorVariantId,
  initialVendorProductGroup,
  host,
  canonicalUrl,
  isIosNative,
  shopNavigationMenu,
  collectionWithProducts
}: IPageProductProps) {
  const router = useRouter();
  const theme = useTheme();

  const [
    {
      blogPosts,
      selectedAttributeFilters,
      selectedVendorVariant,
      vendorVariants,
      attributeFilters
    },
    busy,
    {
      toggleAttributeFilter,
      selectVendorVariant
    }
  ] = useProductGroupState(initialVendorProductGroup, initialVendorVariantId);

  const {
    description,
    images,
    title,
    vendor,
    retailPrice,
    currentPrice,
    taxonomy
  } = selectedVendorVariant;

  const { user } = useContext(UserContext);
  const { open } = useContext(AddToBoardModalContext);

  const [unbounceBannerHeight, setUnbounceBannerHeight] = useState(0);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const reviewRef = useRef<HTMLDivElement>(null);
  const analyticsListId = uuidv4();
  const [activeZoomImage, setActiveZoomImage] = useState<IZoomableImage | null>(null);

  const jsonSchemaVendorVariant = useMemo(
    () => autoSelectVendorVariant(vendorVariants), [vendorVariants]
  );

  const vendorVariantId = useMemo(
    () => selectedVendorVariant.id.toString(), [selectedVendorVariant.id]
  );
  const retailPriceNum = useMemo(() => retailPrice?.price ?? undefined, [retailPrice]);

  const isCartable = useMemo(() => {
    return retailPriceNum !== undefined;
  }, [retailPriceNum]);

  const allAttributesSelected = useMemo(() => {
    return attributeFilters?.length === selectedAttributeFilters?.length;
  }, [attributeFilters, selectedAttributeFilters]);

  useEffect(() => {
    if (selectedVendorVariant) {
      analyticsTrack('product_viewed', {
        type: 'pgp',
        listId: analyticsListId,
        ...getVendorVariantAnalyticsPayload(selectedVendorVariant),
      });
    }
  }, [selectedVendorVariant]);

  const onAttributeFilterChange = (id: string, value: string) => toggleAttributeFilter({
    attributeTypeId: id,
    attributeValue: value
  });

  const isAttributeOptionSelected = (
    option: IAttributeOption
  ) => isChecked(
    option,
    selectedAttributeFilters
  );

  const handleSave = () => {
    open({
      saveItemImageUrl: selectedVendorVariant.images[0]?.uri,
      saveItemId: selectedVendorVariant.id,
      saveItemType: boardItemTypes.VENDOR_VARIANTS,
      source: 'full_page_pgp',
    });
  };

  const handleScrollToReview = () => {
    if (reviewRef.current) {
      reviewRef.current.scrollIntoView({ behavior: 'smooth', inline: 'start' });
    }
  };

  if (!selectedVendorVariant) return <Error data-test="error-organism" message={'Product not found'} />;

  const pageTitle = `${vendor.name} - ${initialVendorProductGroup.collectionName} Collection`;

  const [triggeredAddToCart, setTriggeredAddToCart] = useState(false);
  const { open: openAddedToCartConfirmation } = useContext(AddedToCartConfirmationModalContext);

  const { getCartActionsComponent, isDoneAddingToCart, quantity } = useCartActions(
    selectedVendorVariant,
    getBoardIdFromRouter(router, user!),
    !allAttributesSelected || !isCartable,
    setTriggeredAddToCart
  );

  useEffect(() => {
    if (isDoneAddingToCart && triggeredAddToCart) {
      openAddedToCartConfirmation({ vendorVariant: selectedVendorVariant, quantity });
      setTriggeredAddToCart(false);
    }
  }, [isDoneAddingToCart, triggeredAddToCart]);

  const isNoIndexVendor = NoIndexVendorsList.includes(Number(vendor.id));

  const showProductResults = useMemo(() => {
    return !busy && selectedAttributeFilters &&
      selectedAttributeFilters?.length === attributeFilters?.length &&
      vendorVariants && vendorVariants?.length > 1;
  }, [busy, selectedAttributeFilters, attributeFilters, vendorVariants]);

  const showAttributeFilters = !!(vendorVariants
    && selectedVendorVariant
    && attributeFilters
    && selectedAttributeFilters
    && isAttributeOptionSelected);

  return (
    <VendorVariantContext.Provider value={selectedVendorVariant}>
      {activeZoomImage ? (
        <MobileImageZoom
          activeImage={activeZoomImage}
          onClose={() => setActiveZoomImage(null)}
          onImageSelected={(image) => setActiveZoomImage(image)}
        />
      ) : (
        <DefaultTemplate
          analyticsPageProperties={{
            ecomm_prodid: vendorVariantId,
            ecomm_pagetype: 'product',
            ecomm_totalvalue: currentPrice,
            ecomm_category: taxonomy.title,
            dynx_itemid: vendorVariantId,
            page_version: 'v2'
          }}
          data-test="product__template"
          title={pageTitle}
          description={`Design your space with ${title} on Havenly.com with real interior designers`}
          keywords={[
            'interior design',
            'interior designer',
            'home design',
          ]}
          twitter={{
            card: 'summary_large_image',
            site: '@thehavenly',
          }}
          facebook={createFacebookOpenGraph(
            images,
            retailPriceNum,
            currentPrice,
            pageTitle,
            description,
            `${host}${router.asPath}`,
          )}
          canonicalLink={canonicalUrl}
          isIosNative={isIosNative}
          noIndex={isNoIndexVendor}
          scripts={['/static/affirm.js']}
          jsonSchema={buildJsonSchema(jsonSchemaVendorVariant, `${router.asPath}`, pageTitle)}
          checkUnbounceBannerHeight={
            (height) => setUnbounceBannerHeight(height)
          }
          className={theme.styles.Body}
        >
          <div className={styles.PageWrapper}>
            <ShopNavigationMenu
              shopNavigationMenu={shopNavigationMenu}
              unbounceBannerHeight={unbounceBannerHeight}
            />
            <ShowAt breakpoint="desktop">
              <StickyHeader
                cartActions={getCartActionsComponent()}
                showStickyHeader={showStickyHeader}
                handleScrollToReview={handleScrollToReview}
                unbounceBannerHeight={unbounceBannerHeight}
              />
            </ShowAt>
            <div className={styles.ContentContainer}>
              <If condition={!!selectedVendorVariant.vendor}>
                <ProductBreadcrumbs
                  vendor={selectedVendorVariant.vendor}
                  vendorVariantId={selectedVendorVariant.id.toString()}
                  merchGroupCategory={selectedVendorVariant.merchGroupTaxonomy}
                  className={theme.styles.Caption}
                />
              </If>
              <ProductDetails
                cartActions={getCartActionsComponent()}
                setShowStickyHeader={(state: boolean) => setShowStickyHeader(state)}
                setActiveZoomImage={(image) => setActiveZoomImage(image)}
                onClickReviews={handleScrollToReview}
                busy={busy}
                attributeFilters={showAttributeFilters ? (
                  <>
                    {attributeFilters.length > 0 && (
                    <>
                      <Divider className={styles.Divider} />
                      <AttributesFilter
                        onAttributeFilterChange={onAttributeFilterChange}
                        isAttributeOptionSelected={isAttributeOptionSelected}
                        attributeFilters={attributeFilters}
                        selectedAttributeFilters={selectedAttributeFilters}
                        busy={busy}
                      />
                    </>
                    )}
                    {showProductResults && (
                    <>
                      <Divider className={styles.Divider} />
                      <ProductSelectCards
                        selectedVendorVariantId={selectedVendorVariant.id}
                        vendorVariants={vendorVariants}
                        onSelect={selectVendorVariant}
                      />
                    </>
                    )}
                  </>
                ) : null}
                handleSave={handleSave}
              />
              <FeaturedDesigns
                vendorVariantIds={[selectedVendorVariant.id]}
              />
              <CollectionWithProducts {...collectionWithProducts} />
              <If condition={blogPosts?.length > 0}>
                <OurStories
                  className={styles.OurStories}
                  blogLinks={
                    blogPosts.map(({ largeImage, title: postTitle, url }) => ({
                      image: largeImage,
                      description: postTitle,
                      url
                    }))
                  }
                />
              </If>

              {selectedVendorVariant.reviewStats.count > 0 && (
                <Reviews
                  className={styles.Reviews}
                  summary={selectedVendorVariant.reviewStats}
                  reviews={selectedVendorVariant.reviews}
                  reviewRef={reviewRef}
                />
              )}
              <Footer />
              <HideAt breakpoint="desktop">
                {getCartActionsComponent(true, true)}
              </HideAt>
            </div>
          </div>
        </DefaultTemplate>
      )}
    </VendorVariantContext.Provider>
  );
}

function isChecked(
  option: IAttributeOption,
  selectedAttributeFilters: IAttributeFilterInput[]
) {
  return !!selectedAttributeFilters.find(
    (saf) => (
      saf.attributeTypeId === option.attributeTypeId
      && saf.attributeValue === option.value
    )
  );
}
